import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocalShipping as ReceiveIcon,
  CheckCircle as SuccessIcon,
  WarningAmberOutlined as WarnIcon,
  KeyboardArrowDown as ExpandLotIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import goodsReceiptService from '../../../../services/goodsReceiptService';
import supplierService from '../../../../services/supplierService';
import inventoryBalanceService from '../../../../services/inventoryBalanceService';
import inventoryItemService from '../../../../services/inventoryItemService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import { getPrefill } from '../../../../services/supplierItemLinkService';
import ownershipAccountService from '../../../../services/ownershipAccountService';
import type { InventoryItem, OwnershipAccount, PurchaseOrder, PurchaseOrderLine, VenueLocation } from '../../../../types/venue';
import { formatPoLineSize } from '../../../../utils/poLineSize';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LineEntry {
  poLine: PurchaseOrderLine;
  /** Phase A: true when the inventory item uses lot/batch tracking. */
  lotTracked: boolean;
  prevReceived: number;
  remaining: number;
  receiveNow: string;   // string for controlled input; parse on submit
  unitCost: string;     // optional
  // Phase A lot fields — only sent when lotTracked = true
  lotExpanded: boolean;
  lotNumber: string;
  supplierBatchRef: string;
  expiryDate: string;
  lineError: string | null;
}

type Step = 'entry' | 'review' | 'posting' | 'success' | 'error';

// ── Helpers ───────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().split('T')[0];

// W11 — wraps the shared formatPoLineSize util to match this screen's
// em-dash-on-blank convention. Reads the canonical sizeValue+sizeUnit
// snapshot first, falls back to legacy bottleSize for older PO lines.
const formatLineSize = (line: PurchaseOrderLine | null | undefined): string =>
  formatPoLineSize(line) || '—';

const parseQty = (s: string): number => {
  const n = parseInt(s, 10);
  return isNaN(n) || n < 0 ? 0 : n;
};

// ── Main component ────────────────────────────────────────────────────────────

const ReceivingScreen: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { user } = useAuth();
  const { poId } = useParams<{ poId: string }>();

  const backPath = `/admin/venues/${venueId}/purchasing/${poId}`;

  // ── Load state ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── PO & context ──────────────────────────────────────────────────────────────
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [supplierName, setSupplierName] = useState('');

  // ── Entry form ────────────────────────────────────────────────────────────────
  const [receivedDate, setReceivedDate] = useState(todayISO());
  const [receivedBy, setReceivedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [ownershipAccountId, setOwnershipAccountId] = useState('');
  const [ownershipAccounts, setOwnershipAccounts] = useState<OwnershipAccount[]>([]);
  const [entries, setEntries] = useState<LineEntry[]>([]);
  const [hideFullyReceived, setHideFullyReceived] = useState(true);
  const [entryError, setEntryError] = useState<string | null>(null);

  // ── Flow ──────────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('entry');
  const [postError, setPostError] = useState<string | null>(null);
  const [postErrorCode, setPostErrorCode] = useState<string | null>(null);
  const [postedReceiptId, setPostedReceiptId] = useState<string | null>(null);
  const [newPoStatus, setNewPoStatus] = useState<string | null>(null);

  // Ref for tabbing — we focus first input on mount
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  // ── Load everything ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!poId || !venueId) return;

    const defaultReceivedBy = user?.email ?? user?.firstName ?? '';

    setReceivedBy(defaultReceivedBy);

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // Fix 5: use line-totals endpoint — one call replaces N+1 receipt-line loads.
        // locationName now comes from POLine snapshot — no inventory/location fetches needed.
        const [loadedPo, linesResult, lineTotals, balanceMap] = await Promise.all([
          purchaseOrderService.getOrder(poId),
          purchaseOrderService.getLines(poId),
          purchaseOrderService.getLineTotals(poId),
          inventoryBalanceService.getBalances(venueId).catch(() => ({} as Record<string, any>)),
        ]);

        setPo(loadedPo);

        // Supplier name — non-blocking
        supplierService.getSupplier(loadedPo.supplierId)
          .then(s => setSupplierName(s.name))
          .catch(() => { /* non-blocking */ });

        const prevReceivedMap = new Map<string, number>(
          lineTotals.map(t => [t.purchaseOrderLineId, t.totalReceivedQty]),
        );

        const poLines = linesResult.data ?? [];

        // Fire prefill + item detail calls for all lines in parallel
        const uniqueItemIds = Array.from(new Set(poLines.map(l => l.inventoryItemId)));
        const [prefillResults, itemResults] = await Promise.all([
          Promise.all(
            poLines.map(poLine =>
              getPrefill(venueId, poLine.inventoryItemId, loadedPo.supplierId).catch(() => null),
            ),
          ),
          Promise.all(
            uniqueItemIds.map(id =>
              inventoryItemService.getItem(venueId, id).catch(() => null),
            ),
          ),
        ]);

        const itemMap = new Map<string, InventoryItem>(
          itemResults
            .filter((i): i is InventoryItem => i !== null)
            .map(i => [i._id, i]),
        );

        const built: LineEntry[] = poLines.map((poLine, i) => {
          const prev = prevReceivedMap.get(poLine._id) ?? 0;
          const remaining = Math.max(0, poLine.orderedQty - prev);
          const suggestedCost = prefillResults[i]?.suggestedCost;
          const unitCost =
            poLine.unitPrice != null
              ? String(poLine.unitPrice)
              : suggestedCost != null
              ? String(suggestedCost)
              : balanceMap[poLine.inventoryItemId]?.lastCost != null
              ? String(balanceMap[poLine.inventoryItemId].lastCost)
              : '';
          return {
            poLine,
            lotTracked: itemMap.get(poLine.inventoryItemId)?.lotTracked ?? false,
            prevReceived: prev,
            remaining,
            receiveNow: remaining > 0 ? String(remaining) : '0',
            unitCost,
            lotExpanded: false,
            lotNumber: '',
            supplierBatchRef: '',
            expiryDate: '',
            lineError: null,
          };
        });

        setEntries(built);
      } catch (err: any) {
        setLoadError(err?.response?.data?.message ?? err?.message ?? 'Failed to load purchase order');
      } finally {
        setLoading(false);
      }
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId, venueId]);

  // Load locations and auto-select default
  useEffect(() => {
    if (!venueId) return;
    setLocationsLoading(true);
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setLocationId(id => id || def._id);
      })
      .catch(() => {})
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  // Load ownership accounts — pre-select venue default or the supplier's single CONSIGNED account
  useEffect(() => {
    if (!venueId) return;
    ownershipAccountService.listAccounts(venueId, { active: true })
      .then(res => {
        setOwnershipAccounts(res.data);
        setOwnershipAccountId(prev => {
          if (prev) return prev;
          // Pre-select: if PO supplier has exactly one CONSIGNED account → use it
          const suppId = po?.supplierId;
          if (suppId) {
            const consigned = res.data.filter(
              a => a.type === 'CONSIGNED' && a.supplierId === suppId,
            );
            if (consigned.length === 1) return consigned[0]._id;
          }
          // Fall back to venue default
          const def = res.data.find(a => a.isVenueDefault);
          return def?._id ?? '';
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, po?.supplierId]);

  // Focus first input after load
  useEffect(() => {
    if (!loading && step === 'entry' && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [loading, step]);

  // Resolved location name for display in table cells
  const selectedLocationName = locations.find(l => l._id === locationId)?.name ?? '—';

  // Resolved ownership account for display in review/success
  const selectedOwnerAccount = ownershipAccounts.find(a => a._id === ownershipAccountId) ?? null;

  // ── Entry helpers ─────────────────────────────────────────────────────────────
  const updateEntry = (
    idx: number,
    field: 'receiveNow' | 'unitCost' | 'lotNumber' | 'supplierBatchRef' | 'expiryDate',
    value: string,
  ) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value, lineError: null } : e));
    setEntryError(null);
  };

  const toggleLotExpanded = (idx: number) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, lotExpanded: !e.lotExpanded } : e));
  };

  const visibleEntries = hideFullyReceived
    ? entries.filter(e => e.remaining > 0)
    : entries;

  // Active entries (those with receiveNow > 0) across ALL entries (not just visible)
  const activeEntries = entries.filter(e => parseQty(e.receiveNow) > 0);

  // ── Validation ────────────────────────────────────────────────────────────────
  const handleProceedToReview = () => {
    if (ownershipAccounts.length === 0) {
      setEntryError('No ownership accounts exist for this venue. Create a default Venue Stock account before receiving inventory.');
      return;
    }
    if (!locationId) {
      setEntryError('Select a receiving location before proceeding.');
      return;
    }
    if (!receivedBy.trim()) {
      setEntryError('Received By is required before proceeding.');
      return;
    }
    if (activeEntries.length === 0) {
      setEntryError('Enter a quantity greater than 0 for at least one line.');
      return;
    }
    setEntryError(null);
    setStep('review');
    window.scrollTo(0, 0);
  };

  // ── Over-receipt check ────────────────────────────────────────────────────────
  const isOverReceipt = (entry: LineEntry) => {
    const qty = parseQty(entry.receiveNow);
    return qty > 0 && qty + entry.prevReceived > entry.poLine.orderedQty;
  };

  const overReceiptCount = (entry: LineEntry) => {
    const qty = parseQty(entry.receiveNow);
    return qty + entry.prevReceived - entry.poLine.orderedQty;
  };

  // ── Posting ───────────────────────────────────────────────────────────────────
  const handlePost = async () => {
    if (!poId || !venueId || !po) return;
    setStep('posting');
    setPostError(null);

    let receiptId: string | null = null;
    try {
      // 1. Create draft receipt header
      const receipt = await goodsReceiptService.createReceipt({
        purchaseOrderId: poId,
        venueId,
        locationId,
        receivedDate,
        receivedBy: receivedBy.trim(),
        ...(notes.trim() && { notes: notes.trim() }),
        ...(ownershipAccountId && { ownershipAccountId }),
      });
      receiptId = receipt._id;

      // 2. Create receipt lines via batch endpoint (Fix 6 — one call, validated atomically).
      //    displayName and locationName are generated server-side from PO line snapshots.
      await goodsReceiptService.createReceiptLinesBatch(
        receiptId,
        activeEntries.map(entry => {
          const qty = parseQty(entry.receiveNow);
          const costNum = entry.unitCost !== '' ? parseFloat(entry.unitCost) : undefined;
          const line: {
            purchaseOrderLineId: string;
            inventoryItemId: string;
            receivedQty: number;
            unitCostAtReceipt?: number;
            lotNumber?: string;
            supplierBatchRef?: string;
            expiryDate?: string;
          } = {
            purchaseOrderLineId: entry.poLine._id,
            inventoryItemId: entry.poLine.inventoryItemId,
            receivedQty: qty,
          };
          if (costNum !== undefined && !isNaN(costNum)) line.unitCostAtReceipt = costNum;
          if (entry.lotTracked) {
            if (entry.lotNumber.trim()) line.lotNumber = entry.lotNumber.trim();
            if (entry.supplierBatchRef.trim()) line.supplierBatchRef = entry.supplierBatchRef.trim();
            if (entry.expiryDate) line.expiryDate = entry.expiryDate;
          }
          return line;
        }),
      );

      // 3. Post the receipt (atomic: movements + balance updates + PO status)
      const result = await goodsReceiptService.postReceipt(receiptId);
      setPostedReceiptId(receiptId);
      setNewPoStatus(result.poStatus);
      setStep('success');
      window.scrollTo(0, 0);

    } catch (err: any) {
      // The API uses both `error.code` (nested) and `code` (top-level) in different controllers.
      const errCode = err?.response?.data?.error?.code ?? err?.response?.data?.code ?? null;
      let msg: string;
      if (errCode === 'LOT_NUMBER_ALREADY_EXISTS') {
        msg = 'A lot with this number already exists for one of the items. Use a unique lot number, or leave it blank for auto-generation.';
      } else if (errCode === 'LOT_METADATA_ON_NON_LOT_TRACKED_ITEM') {
        msg = 'Lot details were submitted for an item that does not use lot tracking.';
      } else if (errCode === 'NO_DEFAULT_OWNERSHIP_ACCOUNT') {
        msg = 'No default ownership account found for this venue. Create a Venue Stock account before receiving inventory.';
      } else {
        const rawMsg = err?.response?.data?.message ?? err?.message ?? 'Posting failed';
        const detail = err?.response?.data?.error;
        msg = detail ? `${rawMsg}: ${detail}` : rawMsg;
      }
      setPostError(
        receiptId
          ? `${msg}\n\nA draft receipt was created (ID: ${receiptId}). Return to the purchase order to view or discard it.`
          : msg,
      );
      setPostErrorCode(errCode);
      setStep('error');
    }
  };

  // ── Render: Loading / Error ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Purchase Order
        </Button>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  // ── Render: Success ───────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <SuccessIcon sx={{ fontSize: 56, color: '#1a7f37' }} />
          <Typography variant="h5" fontWeight={700}>Receipt Posted</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {activeEntries.length} {activeEntries.length === 1 ? 'line' : 'lines'} received.
            Stock added to <strong>{selectedLocationName}</strong> under{' '}
            <strong>
              {selectedOwnerAccount?.name ?? 'venue default'}
            </strong>.
            {newPoStatus && (
              <> PO status updated to <strong>{newPoStatus.replace('_', ' ')}</strong>.</>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
            Back to Purchase Order
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              // Reset form for another receipt on same PO
              setStep('entry');
              setNotes('');
              setReceivedDate(todayISO());
              setPostedReceiptId(null);
              setNewPoStatus(null);
              // Re-run load to get updated prevReceived totals
              window.location.reload();
            }}
            sx={{ textTransform: 'none' }}
          >
            Receive More
          </Button>
        </Box>
        {postedReceiptId && (
          <Typography variant="caption" fontFamily="monospace" color="text.disabled" textAlign="center">
            Receipt ID: {postedReceiptId}
          </Typography>
        )}
      </Box>
    );
  }

  // ── Render: Error ─────────────────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start' }}>
          Purchase Order
        </Button>
        <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{postError}</Alert>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
            Back to Purchase Order
          </Button>
          {postErrorCode === 'NO_DEFAULT_OWNERSHIP_ACCOUNT' ? (
            <Button
              variant="contained"
              onClick={() => navigate(`/admin/venues/${venueId}/ownership-accounts/create`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Create Ownership Account
            </Button>
          ) : (
          <Button variant="contained" onClick={() => setStep('review')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Retry
          </Button>
          )}
        </Box>
      </Box>
    );
  }

  // ── Render: Posting (spinner) ─────────────────────────────────────────────────
  if (step === 'posting') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 8 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Posting receipt…</Typography>
      </Box>
    );
  }

  // ── Render: Review ────────────────────────────────────────────────────────────
  if (step === 'review') {
    const hasOverReceipts = activeEntries.some(isOverReceipt);

    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<BackIcon />} onClick={() => setStep('entry')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Back to Entry
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Typography variant="h6" fontWeight={700}>Review &amp; Post Receipt</Typography>
        </Box>

        {/* Receipt header summary */}
        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Supplier</Typography>
            <Typography variant="body2" fontWeight={500}>{supplierName || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Receiving Location</Typography>
            <Typography variant="body2" fontWeight={500}>{selectedLocationName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Received Date</Typography>
            <Typography variant="body2">{receivedDate}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Received By</Typography>
            <Typography variant="body2">{receivedBy}</Typography>
          </Box>
          {notes && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Notes</Typography>
              <Typography variant="body2">{notes}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Ownership Account</Typography>
            <Typography variant="body2" fontWeight={500}>
              {selectedOwnerAccount
                ? `${selectedOwnerAccount.name}${selectedOwnerAccount.isVenueDefault ? ' (default)' : ''}`
                : ownershipAccountId
                ? '…'
                : 'Venue default'}
            </Typography>
          </Box>
        </Paper>

        {hasOverReceipts && (
          <Alert severity="warning" icon={<WarnIcon />}>
            One or more lines exceed the ordered quantity. This is allowed — you will be receiving more than originally ordered.
          </Alert>
        )}

        {/* Review table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Receiving {activeEntries.length} {activeEntries.length === 1 ? 'line' : 'lines'}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Size', 'Location', 'Ordered', 'Prev. Recv.', 'Receiving Now', 'Unit Cost'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeEntries.map(entry => {
                  const over = isOverReceipt(entry);
                  const overCount = over ? overReceiptCount(entry) : 0;
                  return (
                    <TableRow
                      key={entry.poLine._id}
                      sx={over ? { backgroundColor: alpha('#9a6700', 0.05) } : undefined}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{entry.poLine.displayName}</Typography>
                        {entry.lotTracked && (
                          <Box sx={{ mt: 0.25 }}>
                            <Typography variant="caption" color={entry.lotNumber.trim() ? 'text.secondary' : 'text.disabled'}>
                              Lot: {entry.lotNumber.trim() || 'auto-generated'}
                            </Typography>
                            {entry.supplierBatchRef.trim() && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Batch: {entry.supplierBatchRef}
                              </Typography>
                            )}
                            {entry.expiryDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Expiry: {entry.expiryDate}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{formatLineSize(entry.poLine)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{selectedLocationName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.poLine.orderedQty}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{entry.prevReceived}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={600} color={over ? '#9a6700' : undefined}>
                            {parseQty(entry.receiveNow)}
                          </Typography>
                          {over && (
                            <Tooltip title={`Exceeds ordered by ${overCount}`}>
                              <WarnIcon sx={{ fontSize: 14, color: '#9a6700' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {over && (
                          <Typography variant="caption" sx={{ color: '#9a6700', display: 'block' }}>
                            Exceeds ordered by {overCount}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {entry.unitCost !== '' && !isNaN(parseFloat(entry.unitCost))
                            ? `${defaultCurrency}${parseFloat(entry.unitCost).toFixed(2)}`
                            : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={() => setStep('entry')} sx={{ textTransform: 'none' }}>Back</Button>
          <Button
            variant="contained"
            startIcon={<ReceiveIcon />}
            onClick={handlePost}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Post Receipt
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Render: Entry ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Purchase Order
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiveIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>Receive Delivery</Typography>
        </Box>
      </Box>

      {/* Receipt header form */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {supplierName && (
            <Box sx={{ minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Supplier</Typography>
              <Typography variant="body2" fontWeight={500}>{supplierName}</Typography>
            </Box>
          )}
          <TextField
            label="Received Date"
            type="date"
            value={receivedDate}
            onChange={e => setReceivedDate(e.target.value)}
            size="small"
            sx={{ width: 170 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Received By"
            value={receivedBy}
            onChange={e => setReceivedBy(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <TextField
            select
            label="Receiving Location *"
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
            size="small"
            sx={{ width: 200 }}
            disabled={locationsLoading}
            slotProps={{
              input: {
                endAdornment: locationsLoading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null,
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography variant="body2" color="text.disabled">Select location…</Typography>
            </MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            placeholder="Optional"
          />
          {ownershipAccounts.length === 0 ? (
            <Alert
              severity="warning"
              sx={{ flex: 1, minWidth: 240 }}
              action={
                <Button
                  size="small"
                  onClick={() => navigate(`/admin/venues/${venueId}/ownership-accounts/create`)}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 600 }}
                >
                  Create Account
                </Button>
              }
            >
              No ownership accounts configured. Create a default Venue Stock account before receiving inventory.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField
                select
                label="Ownership / Consignment"
                value={ownershipAccountId}
                onChange={e => setOwnershipAccountId(e.target.value)}
                size="small"
                sx={{ width: 240 }}
                helperText="Leave as default unless this is a consigned delivery"
              >
                <MenuItem value="">Use venue default</MenuItem>
                {ownershipAccounts.map(acc => (
                  <MenuItem key={acc._id} value={acc._id}>
                    {acc.name}{acc.isVenueDefault ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
              {(() => {
                const suppId = po?.supplierId;
                const selected = ownershipAccounts.find(a => a._id === ownershipAccountId);
                const isConsignedForSupplier = selected?.type === 'CONSIGNED' && selected?.supplierId === suppId;
                const hasConsignedForSupplier = ownershipAccounts.some(a => a.type === 'CONSIGNED' && a.supplierId === suppId);
                if (isConsignedForSupplier) {
                  return (
                    <Typography variant="caption" sx={{ color: '#0d7d5d', fontWeight: 500 }}>
                      Consignment account detected for this supplier
                    </Typography>
                  );
                }
                if (!hasConsignedForSupplier && suppId) {
                  return (
                    <Button
                      size="small"
                      onClick={() => navigate(`/admin/venues/${venueId}/ownership-accounts/create?type=CONSIGNED&supplierId=${suppId}`)}
                      sx={{ textTransform: 'none', fontSize: '0.72rem', p: 0, alignSelf: 'flex-start', minWidth: 0, color: 'text.secondary' }}
                    >
                      + Set up consignment for this supplier
                    </Button>
                  );
                }
                return null;
              })()}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Lines table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3, py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Lines to Receive</Typography>
            <Typography variant="caption" color="text.secondary">
              {entries.length} line{entries.length !== 1 ? 's' : ''} on this order
              {hideFullyReceived && entries.length !== visibleEntries.length
                ? ` · ${entries.length - visibleEntries.length} hidden (fully received)`
                : ''}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideFullyReceived}
                onChange={e => setHideFullyReceived(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Hide fully received</Typography>}
            sx={{ mr: 0 }}
          />
        </Box>

        {entryError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setEntryError(null)}>{entryError}</Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item', 'Size', 'Location', 'Ordered', 'Prev. Recv.', 'Remaining', 'Receive Now', 'Unit Cost'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25, whiteSpace: 'nowrap' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 5, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">
                        {entries.length === 0
                          ? 'No lines on this purchase order.'
                          : 'All lines have been fully received. Uncheck "Hide fully received" to see them.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleEntries.map((entry, visibleIdx) => {
                  // Map visible entry back to real index in entries array
                  const realIdx = entries.findIndex(e => e.poLine._id === entry.poLine._id);
                  const fullyReceived = entry.remaining <= 0;
                  const qty = parseQty(entry.receiveNow);
                  const over = qty > 0 && qty + entry.prevReceived > entry.poLine.orderedQty;
                  const overCount = over ? overReceiptCount(entry) : 0;

                  return (
                    <React.Fragment key={entry.poLine._id}>
                    <TableRow
                      sx={{
                        opacity: fullyReceived ? 0.5 : 1,
                        backgroundColor: over ? alpha('#9a6700', 0.04) : undefined,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {entry.lotTracked && (
                            <Tooltip title={entry.lotExpanded ? 'Hide lot details' : 'Enter lot details'}>
                              <IconButton
                                size="small"
                                onClick={() => toggleLotExpanded(realIdx)}
                                sx={{ p: 0.25, color: entry.lotExpanded ? 'primary.main' : 'text.disabled' }}
                              >
                                <ExpandLotIcon
                                  fontSize="small"
                                  sx={{ transform: entry.lotExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{entry.poLine.displayName}</Typography>
                            {entry.lotTracked && (
                              <Chip
                                label="Lot tracked"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 16, mt: 0.25, cursor: 'pointer', color: 'text.secondary', borderColor: alpha(theme.palette.divider, 0.6) }}
                                onClick={() => toggleLotExpanded(realIdx)}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatLineSize(entry.poLine)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{selectedLocationName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.poLine.orderedQty}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{entry.prevReceived}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={entry.remaining > 0 ? 600 : undefined}
                          color={entry.remaining <= 0 ? 'text.disabled' : undefined}
                        >
                          {entry.remaining}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 90, py: 0.75 }}>
                        <Box>
                          <TextField
                            inputRef={visibleIdx === 0 ? firstInputRef : undefined}
                            value={entry.receiveNow}
                            onChange={e => updateEntry(realIdx, 'receiveNow', e.target.value)}
                            type="number"
                            size="small"
                            sx={{
                              width: 80,
                              '& input': { textAlign: 'center', fontWeight: 600 },
                            }}
                            slotProps={{ htmlInput: { min: 0, step: 1 } }}
                            error={over}
                          />
                          {over && (
                            <Typography variant="caption" sx={{ color: '#9a6700', display: 'block', mt: 0.25 }}>
                              +{overCount} over
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 100, py: 0.75 }}>
                        <TextField
                          value={entry.unitCost}
                          onChange={e => updateEntry(realIdx, 'unitCost', e.target.value)}
                          type="number"
                          size="small"
                          placeholder="—"
                          sx={{ width: 88 }}
                          slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Lot details sub-row — only for lot-tracked items */}
                    {entry.lotTracked && (
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: entry.lotExpanded ? undefined : 'none' }}>
                          <Collapse in={entry.lotExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Lot details
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                <TextField
                                  label="Lot Number"
                                  size="small"
                                  value={entry.lotNumber}
                                  onChange={e => updateEntry(realIdx, 'lotNumber', e.target.value)}
                                  placeholder="Auto-generated if blank"
                                  helperText="Auto-generated if blank"
                                  sx={{ width: 200 }}
                                  inputProps={{ maxLength: 100 }}
                                />
                                <TextField
                                  label="Supplier Batch Ref"
                                  size="small"
                                  value={entry.supplierBatchRef}
                                  onChange={e => updateEntry(realIdx, 'supplierBatchRef', e.target.value)}
                                  placeholder="Supplier batch ref"
                                  sx={{ width: 200 }}
                                  inputProps={{ maxLength: 100 }}
                                />
                                <TextField
                                  label="Expiry Date"
                                  type="date"
                                  size="small"
                                  value={entry.expiryDate}
                                  onChange={e => updateEntry(realIdx, 'expiryDate', e.target.value)}
                                  sx={{ width: 160 }}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                />
                              </Box>
                              {entry.lineError && (
                                <Alert severity="error" sx={{ py: 0.5 }} onClose={() => updateEntry(realIdx, 'lotNumber', entry.lotNumber)}>
                                  {entry.lineError}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer actions */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', alignItems: 'center' }}>
        {activeEntries.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {activeEntries.length} {activeEntries.length === 1 ? 'line' : 'lines'} ready to receive
          </Typography>
        )}
        <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleProceedToReview}
          startIcon={<ReceiveIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Review &amp; Post Receipt
        </Button>
      </Box>
    </Box>
  );
};

export default ReceivingScreen;
